import type {
  BorescopeDateReading,
  BorescopeFinding,
  BorescopeInspectionRecord,
  BorescopeOverallResult,
} from '@avisoma/shared';

/**
 * Shared presentation helpers for the Borescope Report family (DR-4817). The page view and
 * the entity's consolidated card read their labels from here, so an overall result never
 * renders as its raw enum key in one place and a human phrase in another.
 *
 * The `dateLabel` evidence lane stays out of these helpers; it remains visible through the row
 * inspector and the page's debug payload. `dateRaw` shows only for an unresolved date (DR-5443).
 */

const OVERALL_RESULT_LABELS: Record<BorescopeOverallResult, string> = {
  serviceable: 'Serviceable',
  unserviceable: 'Unserviceable',
  pending: 'Pending',
  unknown: 'Unknown',
};

// A Map rather than an indexed lookup: results reach the UI as raw jsonb, so a stored value of
// `"toString"` must not resolve through Object.prototype.
const OVERALL_RESULT_BY_KEY = new Map<string, string>(Object.entries(OVERALL_RESULT_LABELS));

/**
 * The overall result as shown: the derived class, with the printed phrase beside it when they
 * read differently ("Serviceable (No unserviceable damage noted)"). An unknown class shows the
 * printed phrase alone.
 */
export function formatOverallResult(record: Pick<BorescopeInspectionRecord, 'overallResult' | 'overallResultRaw'>): string | undefined {
  const raw = record.overallResultRaw?.trim() || undefined;
  const label = record.overallResult ? (OVERALL_RESULT_BY_KEY.get(record.overallResult) ?? record.overallResult) : undefined;
  if (label === undefined || record.overallResult === 'unknown') return raw ?? label;
  if (raw === undefined || raw.toLowerCase() === label.toLowerCase()) return label;
  return `${label} (${raw})`;
}

/** How a non-trivial date reading is named beside the date (DR-5443); `unambiguous` needs no note. */
const DATE_READING_LABELS: Record<Exclude<BorescopeDateReading, 'unambiguous' | 'ambiguous'>, string> = {
  'day-first-issuer': 'day-first (issuer location)',
  'month-first-issuer': 'month-first (issuer location)',
  'day-first-pack-fit': 'day-first (pack fit)',
  'month-first-pack-fit': 'month-first (pack fit)',
};

// A Map for the same raw-jsonb reason as the overall result labels.
const DATE_READING_BY_KEY = new Map<string, string>(Object.entries(DATE_READING_LABELS));

/**
 * The inspection date as shown (DR-5443): the ISO date, with how an ambiguous numeric print was
 * read beside it ("2026-04-10 · day-first (issuer location)"). A date whose order could not be
 * resolved shows the printed token and asks for review ("10/04/2026 · order unresolved, review").
 */
export function formatBorescopeDate(
  record: Pick<BorescopeInspectionRecord, 'date' | 'dateRaw' | 'dateReading'>,
): string | undefined {
  if (record.date) {
    const reading = record.dateReading ? DATE_READING_BY_KEY.get(record.dateReading) : undefined;
    return reading ? `${record.date} · ${reading}` : record.date;
  }
  if (record.dateReading === 'ambiguous' && record.dateRaw) return `${record.dateRaw} · order unresolved, review`;
  return undefined;
}

/** An engine clock as a label value. `0` is a valid TSN/CSN and must render. */
export function formatBorescopeClock(value: number | undefined): string | undefined {
  return value == null ? undefined : String(value);
}

/** The finding rows, guarded: the value reaches the UI as raw jsonb. */
export function borescopeFindings(record: Pick<BorescopeInspectionRecord, 'findings'>): BorescopeFinding[] {
  return Array.isArray(record.findings) ? record.findings.filter((row) => typeof row === 'object' && row !== null) : [];
}

/** "3 — FAN BLADES, HPC St1 +1": the finding count and the first two areas, or `undefined` when none. */
export function summariseFindings(record: Pick<BorescopeInspectionRecord, 'findings'>): string | undefined {
  const rows = borescopeFindings(record);
  if (rows.length === 0) return undefined;
  const areas = rows.map((row) => row.area?.trim()).filter((area): area is string => !!area);
  const shown = areas.slice(0, 2).join(', ');
  const rest = areas.length > 2 ? ` +${areas.length - 2}` : '';
  return shown === '' ? String(rows.length) : `${rows.length} — ${shown}${rest}`;
}

/**
 * The record's present scalars as label/value pairs, in reading order. Absent fields are dropped
 * so a stage page renders as a short card rather than a grid of em dashes.
 */
export function presentBorescopeFields(record: BorescopeInspectionRecord): [string, string][] {
  const fields: [string, string | undefined][] = [
    ['Subject', record.subject],
    ['ESN', record.esn],
    ['Date', formatBorescopeDate(record)],
    ['Work Order', record.workOrder],
    ['Report Ref', record.reportRef],
    ['Organisation', record.organisation],
    ['Organisation Location', record.organisationLocation],
    ['TSN', formatBorescopeClock(record.tsn)],
    ['CSN', formatBorescopeClock(record.csn)],
    ['Overall Result', formatOverallResult(record)],
  ];
  return fields.filter((entry): entry is [string, string] => entry[1] != null && entry[1] !== '');
}
